import { skipToken } from "@reduxjs/toolkit/query";
import {
  shiftDateRangeToPreviousPeriod,
  type PlaybookScope,
  type SignalsPageFilter,
} from "@repo/app-types/entities/filters";
import type { GroupId } from "@repo/app-types/entities/group";
import type { PlaybookSelection } from "@repo/app-types/entities/playbook";
import type {
  Signal,
  SignalCreatePayload,
  SignalDrilldown,
  SignalId,
  SignalPayload,
  SignalsOverview,
} from "@repo/app-types/entities/signal";
import {
  testSignalProposalToolOutputSchema,
  type TestSignalProposalToolOutput,
} from "@repo/app-types/entities/signalConfigurationTools";
import { safeParseJSONWithSchema } from "@repo/app-types/helpers/zodHelpers";
import { fetchEventStream } from "@repo/ui/api/eventStream";
import { RECENT_MATCHES_DISPLAY_LIMIT } from "../../features/transcriptViewer/matchListItem.js";
import {
  buildListTag,
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";
import { apiBaseUrl, getApiAuthorizationHeaderValue } from "../shared.js";

/** Arg shape for the signals stats hooks — scope + predicates, passed side-by-side. */
export interface SignalStatsQueryArg {
  scope: PlaybookScope;
  filters: readonly SignalsPageFilter[];
}

/**
 * The mutation's response, or `undefined` when it failed. The caller that
 * fired the mutation reports the failure through its own `unwrap`, so reading
 * it here must not reject a second time as an unhandled rejection.
 */
async function readFulfilled<T>(
  queryFulfilled: Promise<{ data: T }>,
): Promise<T | undefined> {
  return queryFulfilled.then(
    ({ data }) => data,
    () => undefined,
  );
}

const signalApi = api.injectEndpoints({
  endpoints: (builder) => ({
    signalDrilldown: builder.query<SignalDrilldown, SignalStatsQueryArg>({
      query: ({ scope, filters }) => {
        const signalFilter = filters.find((f) => f.type === "signal");
        if (!signalFilter) {
          throw new Error(
            "signalDrilldown query requires a signal filter in filters",
          );
        }
        return {
          method: "POST",
          url: `/signals/${encodeURIComponent(signalFilter.id)}/detail`,
          body: { scope, filters, limit: RECENT_MATCHES_DISPLAY_LIMIT },
        };
      },
      providesTags: (_result, _error, { filters }) => {
        const signalFilter = filters.find((f) => f.type === "signal");
        return signalFilter
          ? [{ type: TagType.Signal, id: signalFilter.id }]
          : [];
      },
    }),
    signalsOverview: builder.query<SignalsOverview[], SignalStatsQueryArg>({
      query: ({ scope, filters }) => ({
        method: "POST",
        url: "/signals/overview",
        body: { scope, filters },
      }),
      providesTags: (result) => {
        const signalTag = TagType.Signal;
        return [
          { type: signalTag, id: "LIST" },
          ...(result?.map((s) => ({
            type: signalTag,
            id: s.signalId,
          })) ?? []),
        ];
      },
    }),
    allSignals: builder.query<Signal[], void>({
      query: () => `/signals`,
      providesTags: (result) => getTagsProvidedByList(TagType.Signal, result),
    }),
    getSignal: builder.query<Signal, SignalId>({
      query: (id) => `/signals/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) =>
        getTagsProvidedByEntity(TagType.Signal, id),
    }),
    /** Creates the signal with its definition live at once. */
    createSignal: builder.mutation<Signal, SignalCreatePayload>({
      query: (values) => ({
        method: "POST",
        url: `/signals`,
        body: values,
      }),
      onQueryStarted: async (_values, { dispatch, queryFulfilled }) => {
        const created = await readFulfilled(queryFulfilled);
        if (created) {
          dispatch(
            signalApi.util.upsertQueryData("getSignal", created.id, created),
          );
        }
      },
      invalidatesTags: () => getTagsInvalidatedByCreate(TagType.Signal),
    }),
    /**
     * Saves the signal as a new live version. The response replaces the cached
     * signal; only the list is refetched.
     */
    saveSignal: builder.mutation<Signal, SignalPayload & { id: SignalId }>({
      query: ({ id, ...body }) => ({
        method: "PATCH",
        url: `/signals/${encodeURIComponent(id)}`,
        body,
      }),
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        const saved = await readFulfilled(queryFulfilled);
        if (saved) {
          dispatch(signalApi.util.upsertQueryData("getSignal", id, saved));
        }
      },
      invalidatesTags: () => [buildListTag(TagType.Signal, [])],
    }),
    deleteSignal: builder.mutation<void, SignalId>({
      query: (id) => ({
        method: "DELETE",
        url: `/signals/${encodeURIComponent(id)}`,
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          signalApi.util.updateQueryData("allSignals", undefined, (draft) => {
            const index = draft.findIndex((s) => s.id === id);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, id) =>
        getTagsInvalidatedByDelete(TagType.Signal, id),
    }),
    testSignalProposal: builder.mutation<
      TestSignalProposalToolOutput,
      {
        signalId?: SignalId;
        signal: SignalPayload;
        filters: {
          playbookScope: PlaybookSelection;
          teammateGroupIds?: readonly GroupId[];
          lastRecordedAtRange?: {
            before?: string;
            after?: string;
          };
        };
      }
    >({
      queryFn: async (body, { signal }) => {
        try {
          const url = new URL("/signals/test-stream", apiBaseUrl);
          const events = fetchEventStream(url, {
            headers: {
              authorization: await getApiAuthorizationHeaderValue(),
              "x-alpharun-commit-sha": __COMMIT_SHA__,
            },
            body,
            signal,
          });

          let result: TestSignalProposalToolOutput | undefined;
          let serverErrorMessage: string | undefined;
          for await (const { event, data } of events) {
            if (event === "result") {
              result =
                safeParseJSONWithSchema(
                  data,
                  testSignalProposalToolOutputSchema,
                ) ?? undefined;
            } else if (event === "error") {
              const parsed = JSON.parse(data) as { message?: string };
              serverErrorMessage = parsed.message ?? "Unknown error";
            }
          }

          if (serverErrorMessage) {
            throw new Error(serverErrorMessage);
          }

          if (!result) {
            throw new Error("Stream ended without a test signal result");
          }

          return { data: result };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error
                  ? error.message
                  : "Failed to test signal",
            },
          };
        }
      },
    }),
  }),
});

export const {
  useSignalDrilldownQuery: useSignalDrilldownBaseQuery,
  useSignalsOverviewQuery: useSignalsOverviewBaseQuery,
  useAllSignalsQuery,
  useGetSignalQuery,
  useCreateSignalMutation,
  useSaveSignalMutation,
  useDeleteSignalMutation,
  useTestSignalProposalMutation,
} = signalApi;

export interface SignalsOverviewWithPrior {
  current: SignalsOverview[];
  prior: SignalsOverview[];
}

export interface SignalDrilldownWithPrior {
  current: SignalDrilldown;
  prior: SignalDrilldown;
}

/**
 * Compose current + prior period data by firing the endpoint twice with
 * same-length-immediately-preceding date ranges. Deliberately thin (no
 * HTTP-layer decomposition to share grading-count cache) because users
 * typically change filters on drilldown navigation, so cross-page cache hits
 * are rare; the extra round trip on filter change would cost more than it
 * saves.
 */
export function useSignalsOverviewQuery(args: SignalStatsQueryArg): {
  data?: SignalsOverviewWithPrior;
  isLoading: boolean;
  isFetching: boolean;
} {
  const current = useSignalsOverviewBaseQuery(args);
  const prior = useSignalsOverviewBaseQuery({
    ...args,
    scope: {
      ...args.scope,
      dateRange: shiftDateRangeToPreviousPeriod(args.scope.dateRange),
    },
  });
  const isLoading = current.isLoading || prior.isLoading;
  const isFetching = current.isFetching || prior.isFetching;
  if (!current.data || !prior.data) {
    return { isLoading, isFetching };
  }
  return {
    isLoading,
    isFetching,
    data: { current: current.data, prior: prior.data },
  };
}

export function useSignalDrilldownQuery(
  args: SignalStatsQueryArg | typeof skipToken,
): {
  data?: SignalDrilldownWithPrior;
  isLoading: boolean;
  isFetching: boolean;
} {
  const current = useSignalDrilldownBaseQuery(args);
  const prior = useSignalDrilldownBaseQuery(
    args === skipToken
      ? skipToken
      : {
          ...args,
          scope: {
            ...args.scope,
            dateRange: shiftDateRangeToPreviousPeriod(args.scope.dateRange),
          },
        },
  );
  const isLoading = current.isLoading || prior.isLoading;
  const isFetching = current.isFetching || prior.isFetching;
  if (!current.data || !prior.data) {
    return { isLoading, isFetching };
  }
  return {
    isLoading,
    isFetching,
    data: { current: current.data, prior: prior.data },
  };
}
